import {
  EntityLinks,
  Goal,
  GoalDeadlineRowsMap,
  GoalProgressCount,
  GoalTreeIndex,
  ID,
  LifeArea,
  TaskFilterOptions,
} from '@griply/types';
import {
  buildGoalDeadlineRows,
  DeadlineGoal,
  filterGoalsByFilterOptions,
  getDeadlineGoals,
} from '@griply/utils';
import { useMemo } from 'react';

const emptyGoals: DeadlineGoal[] = [];

export type UseGoalDeadlineRowsOptions = {
  goals: Goal[];
  lifeAreas: LifeArea[];
  links: EntityLinks;
  filters: TaskFilterOptions[];
  goalProgressCounts?: Map<ID, GoalProgressCount>;
  goalTreeIndex?: GoalTreeIndex;
  showCompleted?: boolean;
  enabled?: boolean;
};

export type GoalDeadlineRows = {
  deadlineGoals: DeadlineGoal[];
  goalRows: GoalDeadlineRowsMap;
};

export const useGoalDeadlineRows = ({
  goals,
  lifeAreas,
  links,
  filters,
  goalProgressCounts,
  goalTreeIndex,
  showCompleted,
  enabled,
}: UseGoalDeadlineRowsOptions): GoalDeadlineRows => {
  const deadlineGoals = useMemo(
    () =>
      enabled
        ? filterGoalsByFilterOptions(
            // getDeadlineGoals walks ancestors, so it must see every goal.
            getDeadlineGoals(goals, { showCompleted, links }),
            filters,
          )
        : emptyGoals,
    [enabled, goals, links, filters, showCompleted],
  );

  // Per goal, never per rendered row.
  const goalRows = useMemo(
    () =>
      buildGoalDeadlineRows(deadlineGoals, {
        goals,
        lifeAreas,
        links,
        goalProgressCounts,
        goalTreeIndex,
      }),
    [deadlineGoals, goals, lifeAreas, links, goalProgressCounts, goalTreeIndex],
  );

  return { deadlineGoals, goalRows };
};
